import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef
} from "react";
import { User } from "@/core/models/global/User";
import { authApi } from "@/services/api";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { signalRService } from "@/services/signalr";
import { LoginResponse } from "@/core/models/auth/LoginResponse";
import { RegisterResponse } from "@/core/models/auth/RegisterResponse";
import { UserResponse } from "@/core/models/auth/UserResponse";
import { v4 as uuidv4 } from "uuid";
import { RegisterRequest } from "@/core/models/auth/RegisterRequest";
import { SectionId, getSectionDetail, listSections, SectionIcon, SectionDetail } from "@/core/global/SectionRegistry";
import { BASE_PATH } from "@/utils/basePath";
import { clearApplicantStorage } from "@/utils/clearApplicantStorage";
import { toast } from "react-toastify";

const GOD_MODE_KEY = "dev_god_mode";

interface GlobalContextProps {
    // Non-reactive user
    getUser: () => User | null;
    setUser: (value: User | null) => void;

    // Non-reactive getters/setters
    getIsAuthenticated: () => boolean;
    setIsAuthenticated: (value: boolean) => void;
    getIsVerified: () => boolean;
    setIsVerified: (value: boolean) => void;
    getIsLoading: () => boolean;
    setIsLoading: (value: boolean) => void;
    getIsLoggedOut: () => boolean;
    // Reactive mirrors — subscribe without polling
    isAuthenticated: boolean;
    isVerified: boolean;
    isLoading: boolean;
    isLoggedOut: boolean;
    login: (_identifier: string, _password: string) => Promise<void>;
    logout: () => Promise<void>;
    register: (
        formData: RegisterRequest,
        registerCallBack: (response: UserResponse) => void
    ) => Promise<void>;
    updateFirstVisit: () => Promise<void>;
    refreshUser: () => Promise<void>;
    getHasDashboardLayoutRun: () => boolean;
    setHasDashboardLayoutRun: (value: boolean) => void;
    getCurrentMode: () => string;
    getSignalRConnectionState: () => string;
    setSignalRConnectionState: (state: string) => void;
    mountedObjectIds: string[]; // deprecated compat snapshot
    registerMountedObject: (id?: string) => string;
    unregisterMountedObject: (id: string) => void;
    isObjectMounted: (id: string) => boolean;
    getMountedObjectIds: () => string[];
    SectionId;
    getSectionDetail;
    listSections: () => SectionDetail[];
    SectionIcon;
    PreviewMode: boolean;
    getFirstUncompletedPage: () => string | null;
    setFirstUncompletedPage: (value: string | null) => void;
    /** Dev-only: bypasses profile-completion guards so all pages/panels are accessible. */
    godMode: boolean;
    setGodMode: (value: boolean) => void;
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined);

// Preview mode is a build/feature flag, so it is read from the flag and from the build mode -- never
// from the deployment path. It used to also test VITE_BASE_URL.includes("SEPDevPreview"), which tied
// the feature to where the app happened to be mounted: the preview build could not be deployed
// anywhere else, and mounting it at site root silently turned the feature off.
// Both inputs are fixed at build time, so this is a module constant rather than per-render state.
const envPreview = String(import.meta.env.VITE_PREVIEW_MODE ?? "").toLowerCase() === "true";
const PreviewMode = envPreview || import.meta.env.MODE === "SEPDevPreview";

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();

    // Non-reactive user
    const userRef = useRef<User | null>(null);
    const getUser = useCallback(() => userRef.current, []);
    const setUser = useCallback((val: User | null) => { userRef.current = val; }, []);

    // Refs for synchronous reads inside async callbacks (no stale-closure risk)
    // State mirrors drive re-renders — setters write both so they stay in sync
    const isAuthenticatedRef = useRef<boolean>(false);
    const [isAuthenticatedState, setIsAuthenticatedState] = useState<boolean>(false);
    const setIsAuthenticated = useCallback((val: boolean) => {
        isAuthenticatedRef.current = val;
        setIsAuthenticatedState(val);
    }, []);
    const getIsAuthenticated = useCallback(() => isAuthenticatedRef.current, []);

    const isVerifiedRef = useRef<boolean>(false);
    const [isVerifiedState, setIsVerifiedState] = useState<boolean>(false);
    const setIsVerified = useCallback((val: boolean) => {
        isVerifiedRef.current = val;
        setIsVerifiedState(val);
    }, []);
    const getIsVerified = useCallback(() => isVerifiedRef.current, []);

    const isLoadingRef = useRef<boolean>(true);
    const [isLoadingState, setIsLoadingState] = useState<boolean>(true);
    const setIsLoading = useCallback((val: boolean) => {
        isLoadingRef.current = val;
        setIsLoadingState(val);
    }, []);
    const getIsLoading = useCallback(() => isLoadingRef.current, []);

    const isLoggedOutRef = useRef<boolean>(false);
    const [isLoggedOutState, setIsLoggedOutState] = useState<boolean>(false);
    const getIsLoggedOut = useCallback(() => isLoggedOutRef.current, []);

    const firstUncompletedPageRef = useRef<string | null>(null);
    const setFirstUncompletedPage = useCallback((val: string | null) => { firstUncompletedPageRef.current = val; }, []);
    const getFirstUncompletedPage = useCallback(() => firstUncompletedPageRef.current, []);

    // God Mode — dev only, persisted in sessionStorage
    const [godMode, setGodModeState] = useState<boolean>(
        import.meta.env.DEV
            ? sessionStorage.getItem(GOD_MODE_KEY) === "true"
            : false
    );
    const setGodMode = useCallback((val: boolean) => {
        if (!import.meta.env.DEV) return;
        sessionStorage.setItem(GOD_MODE_KEY, String(val));
        setGodModeState(val);
    }, []);
    
    const hasDashboardLayoutRunRef = useRef<boolean>(false);
    const setHasDashboardLayoutRun = useCallback((val: boolean) => { hasDashboardLayoutRunRef.current = val; }, []);
    const getHasDashboardLayoutRun = useCallback(() => hasDashboardLayoutRunRef.current, []);

    const currentModeRef = useRef<string>("unknown");
    const getCurrentMode = useCallback(() => currentModeRef.current, []);

    const signalRConnectionStateRef = useRef<string>("Disconnected");
    const setSignalRConnectionState = useCallback((state: string) => { signalRConnectionStateRef.current = state; }, []);
    const getSignalRConnectionState = useCallback(() => signalRConnectionStateRef.current, []);

    const isInitializedRef = useRef<boolean>(false);

    // Mounted objects tracking via ref
    const mountedObjectIdsRef = useRef<Set<string>>(new Set());
    const getMountedObjectIds = useCallback(() => Array.from(mountedObjectIdsRef.current), []);

    const registerMountedObject = useCallback((id?: string) => {
        const newId = id ?? uuidv4();
        if (!mountedObjectIdsRef.current.has(newId)) {
            mountedObjectIdsRef.current.add(newId);
        }
        return newId;
    }, []);

    const unregisterMountedObject = useCallback((id: string) => {
        if (mountedObjectIdsRef.current.has(id)) {
            mountedObjectIdsRef.current.delete(id);
        }
    }, []);

    const isObjectMounted = useCallback((id: string) => {
        return mountedObjectIdsRef.current.has(id);
    }, []);

    // Upgrades this session when its e-mail address has been verified (from this or another browser). Assigned below, after
    // refreshUser exists.
    const upgradeAfterVerificationRef = useRef<() => Promise<void>>(async () => {});

    // SignalR event handlers
    useEffect(() => {
        // The e-mail was verified (often from another browser). The push carries no user data: this session asks the server,
        // which re-issues its cookie as verified, then loads its own user data. The person stays signed in.
        const onEmailVerified = () => {
            void upgradeAfterVerificationRef.current();
        };
        const onUserUpdated = (userData: User) => {
            const userInstance = User.fromJSON(userData);
            setUser(userInstance);
            setIsVerified(userData.isEmailVerified ?? false);
        };
        const onConnectionStateChanged = (state: string) => {
            setSignalRConnectionState(state);
        };

        signalRService.onEmailVerified(onEmailVerified);
        signalRService.onUserUpdated(onUserUpdated);
        signalRService.onConnectionStateChanged(onConnectionStateChanged);
        setSignalRConnectionState(signalRService.getConnectionState());

        return () => {
            signalRService.offEmailVerified();
            signalRService.offUserUpdated();
            signalRService.offConnectionStateChanged();
        };
    }, [setUser, setIsVerified, setSignalRConnectionState]);

    // Health monitoring (read user from ref)
    useEffect(() => {
        let didStartMonitoring = false;
        const u = getUser();
        if (u && u.email) {
            signalRService.enableAutoReconnect();
            signalRService.startHealthMonitoring();
            didStartMonitoring = true;
        } else {
            signalRService.stopHealthMonitoring();
        }
        return () => {
            if (didStartMonitoring) {
                signalRService.stopHealthMonitoring();
            }
        };
    }, [getUser]);

    // SignalR connection lifecycle (read user from ref)
    useEffect(() => {
        const u = getUser();
        if (u && u.email) {
            const setupSignalR = async () => {
                try {
                    signalRService.enableAutoReconnect();
                    if (!signalRService.isConnected()) {
                        await signalRService.startConnection();
                    }
                    await signalRService.joinEmailGroup(u?.email ?? null);
                } catch (error) {
                    console.error("Failed to setup SignalR connection:", error);
                }
            };
            setupSignalR();
        } else if (!u) {
            const cleanupSignalR = async () => {
                try {
                    const currentEmail = signalRService.getCurrentEmail();
                    if (currentEmail) {
                        await signalRService.leaveEmailGroup(currentEmail);
                    }
                    signalRService.disableAutoReconnect();
                    await signalRService.stopConnection();
                    signalRService.stopHealthMonitoring();
                } catch (error) {
                    console.error("Failed to cleanup SignalR connection:", error);
                }
            };
            cleanupSignalR();
        }
        // isAuthenticatedState is the reactive trigger; user itself is ref-only and never
        // causes a re-render, so without this dependency the effect only ever runs once at
        // mount (before login/register set the ref), and joinEmailGroup is never called for
        // the rest of the session — the SignalR push then never reaches this
        // client until a full page reload re-runs this effect against an already-authenticated
        // cookie session. getUser is a stable ref reader and never re-triggers it.
    }, [isAuthenticatedState, getUser]);

    // Initialization
    useEffect(() => {
        currentModeRef.current = import.meta.env.MODE || "unknown";
        const initializeAuth = async () => {
            try {
                const u = getUser();
                if (PreviewMode && !u) {
                    const previewEmail = (import.meta.env.VITE_PREVIEW_EMAIL ?? "").toString() || "preview@testsep.com";
                    const previewPassword = (import.meta.env.VITE_PREVIEW_PASSWORD ?? "").toString();
                    if (previewPassword) {
                        try {
                            const loginRes = await authApi.login({ identifier: previewEmail, password: previewPassword }) as LoginResponse;
                            loginSuccessRef.current(loginRes);
                        } catch (e) {
                            console.warn("Preview login failed, will fallback to anonymous preview user.", e);
                        }
                    }
                    if (!getUser()) {
                        const previewUser = new User({
                            applicantId: 0,
                            email: previewEmail,
                            firstVisit: false,
                            isEmailVerified: true,
                        } as any);
                        setUser(previewUser);
                        setIsVerified(true);
                    }
                }

                const userData = await authApi.getCurrentUser();
                if (!userData) {
                    return;
                }
                if (userData) {
                    const userInstance = User.fromJSON(userData);
                    setUser(userInstance);
                    setIsAuthenticated(true);
                    setIsVerified(userData.isEmailVerified ?? false);
                    isLoggedOutRef.current = false;
                    setIsLoggedOutState(false);
                } else {
                    Cookies.remove("authToken");
                    Cookies.remove("applicantId");
                }
            } catch (error) {
                console.error("Error during auth initialization:", error);
                Cookies.remove("authToken");
                Cookies.remove("applicantId");
                clearApplicantStorage();
            } finally {
                setIsLoading(false);
                isInitializedRef.current = true;
            }
        };

        if (!isInitializedRef.current) {
            initializeAuth();
        }

        const initTimeout = setTimeout(() => {
            if (getIsLoading() && !isInitializedRef.current) {
                setIsLoading(false);
                isInitializedRef.current = true;
            }
        }, 10000);

        return () => clearTimeout(initTimeout);
        // Runs once at mount: every dependency below is a stable useCallback([]) ref accessor.
    }, [getUser, setUser, setIsAuthenticated, setIsVerified, setIsLoading, getIsLoading]);

    // The signed-in applicant is read once, at page load, and kept in memory. The sign-in cookie is shared by every tab of the
    // browser, so signing in as another applicant in a second tab leaves this tab showing the first one: its requests then name
    // the first applicant while the cookie says the second, and the server answers 403 (UAT SEP-016, an SSN save that failed once
    // and worked after a reload). When the tab is shown or focused again, ask the server who is signed in and reload on a change.
    useEffect(() => {
        if (PreviewMode || !isAuthenticatedState) return;

        let lastCheck = 0;
        let reloading = false;
        const checkSignedInApplicant = async () => {
            const known = getUser()?.applicantId;
            const now = Date.now();
            if (reloading || !known || document.visibilityState !== "visible" || now - lastCheck < 5000) return;
            lastCheck = now;
            try {
                const current = await authApi.getCurrentUser();
                const currentId = current?.applicantId;
                if (currentId && Number(currentId) !== Number(known)) {
                    reloading = true;
                    toast.info("You signed in as a different account in another tab. This tab will reload to show it.");
                    window.setTimeout(() => window.location.reload(), 2500);
                }
            } catch {
                // Signed out, offline or a server error: the normal request handling deals with those.
            }
        };

        document.addEventListener("visibilitychange", checkSignedInApplicant);
        window.addEventListener("focus", checkSignedInApplicant);
        return () => {
            document.removeEventListener("visibilitychange", checkSignedInApplicant);
            window.removeEventListener("focus", checkSignedInApplicant);
        };
    }, [isAuthenticatedState, getUser]);

    const logout = useCallback(async (): Promise<void> => {
        try {
            const u = getUser();
            if (u?.email && signalRService.isConnected()) {
                await signalRService.leaveEmailGroup(u.email);
            }
            try {
                signalRService.disableAutoReconnect();
                signalRService.stopHealthMonitoring();
                if (signalRService.isConnected()) {
                    await signalRService.stopConnection();
                }
            } catch (err) {
                console.error("Error stopping SignalR during logout:", err);
            }
            await authApi.logout(() => {});
        } catch (error) {
            console.error("Logout failed:", error);
        }
        isLoggedOutRef.current = true;
        setIsLoggedOutState(true);
        setUser(null);
        setIsAuthenticated(false);
        setIsVerified(false);
        setIsLoading(false);
        Cookies.remove("authToken");
        Cookies.remove("applicantId");
        clearApplicantStorage();
        navigate("/", { replace: true });
    }, [getUser, setUser, setIsAuthenticated, setIsVerified, setIsLoading, navigate]);

    const refreshUser = useCallback(async () => {
        try {
            const user = await authApi.getCurrentUser();
            if (user) {
                setUser(user);
                setIsAuthenticated(true);
                setIsVerified(Boolean(user.isEmailVerified));
                sessionStorage.setItem("registeredEmail", user?.email ?? "");
            } else {
                if (!PreviewMode) {
                    logout();
                }
            }
        } catch (e) {
            if (e instanceof Error && e.message.includes("401")) return;
            if (!PreviewMode) {
                logout();
            }
        }
    }, [logout, setUser, setIsAuthenticated, setIsVerified]);

    type AnyUserResponse = RegisterResponse | LoginResponse;

    const loginSuccess = useCallback((userResponse: AnyUserResponse) => {
      
        if (userResponse?.user?.applicantId && userResponse.token === "authenticated") {
            const userInstance = new User(userResponse.user);
            setUser(userInstance);
            // Authenticated now, as the session-restore path does: without it PrivateRoute sends the new session back to
            // the login page until refreshUser() finishes, then on to the dashboard (UAT: login "kicks you out").
            setIsAuthenticated(true);
            isLoggedOutRef.current = false;
            setIsLoggedOutState(false);
            setIsVerified(userResponse.user.isEmailVerified ?? false);
            Cookies.set("authToken", userResponse.token, {
                expires: 7,
                secure: window.location.protocol === "https:",
                sameSite: "Lax",
            });
            Cookies.set("applicantId", String(userResponse.user.applicantId), {
                expires: 7,
                secure: window.location.protocol === "https:",
                sameSite: "Lax",
            });
            sessionStorage.setItem("registeredEmail", userResponse.user.email || "");
            refreshUser();
            setIsLoading(false);
            navigate("/dashboard/")
        } else {
            console.error("Login failed: Invalid response.", userResponse);
            throw new Error("Login failed: Invalid response.");
        }
    }, [setUser, setIsAuthenticated, setIsVerified, refreshUser, setIsLoading, navigate]);
    // Read by the mount-only initialization effect above. loginSuccess changes whenever navigate does, and listing it there
    // would re-run initialization (and re-arm its 10-second timeout) on those renders.
    const loginSuccessRef = useRef(loginSuccess);
    loginSuccessRef.current = loginSuccess;

    const login = useCallback(async (_identifier: string, _password: string) => {
        try
        {
            const loginRes = (await authApi.login({
                identifier: _identifier,
                password: _password,
            })) as LoginResponse;
            loginSuccess(loginRes as LoginResponse);
        } catch (error) {
            console.error("Login error:", error);
            throw new Error("Login failed: Something went wrong.");
        }
    }, [loginSuccess]);

    // The e-mailed verification link is often opened in another browser, which updates only its own session. While this
    // browser is signed in but unverified, ask the server (a database read) every few seconds and when the tab regains
    // focus; the SignalR push is the fast path, this is the one that cannot be missed (dropped socket, sleeping tab).
    // Once verified, refreshUser flips isVerified and the verify page's own effect moves on to the dashboard.
    // refreshUser is read through a ref: it changes whenever navigate does (via logout), and an effect that listed it would clear
    // and restart the 5-second timer on those renders, so the poll could keep being pushed back and never fire.
    const refreshUserRef = useRef(refreshUser);
    refreshUserRef.current = refreshUser;

    useEffect(() => {
        if (PreviewMode || !isAuthenticatedState || isVerifiedState) return;

        let cancelled = false;
        let checking = false;
        const check = async () => {
            if (checking || cancelled || document.visibilityState === "hidden") return;
            checking = true;
            try {
                if (await authApi.getVerificationStatus()) {
                    if (!cancelled) await refreshUserRef.current();
                }
            } catch {
                // offline, or the session ended: the next tick tries again, and a real sign-out is handled elsewhere
            } finally {
                checking = false;
            }
        };

        const timer = window.setInterval(check, 5000);
        const onFocus = () => void check();
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, [isAuthenticatedState, isVerifiedState]);

    upgradeAfterVerificationRef.current = async () => {
        // Only a signed-in, still-unverified session has anything to upgrade; the signal also reaches a browser with nobody
        // signed in and sessions that are already verified.
        if (isLoggedOutRef.current || !isAuthenticatedRef.current || isVerifiedRef.current) return;
        try {
            if (await authApi.getVerificationStatus()) await refreshUser();
        } catch {
            // offline, or the session ended: the poll above tries again
        }
    };

    const updateFirstVisit = useCallback(async () => {
        try {
            await authApi.updateFirstVisit();
            await new Promise((resolve) => setTimeout(resolve, 100));
            await refreshUser();
        } catch (error) {
            console.error("Failed to update first visit:", error);
            throw error;
        }
    }, [refreshUser]);

    const register = useCallback(async (
        formData: RegisterRequest,
        registerCallBack: (response: RegisterResponse) => void
    ) => {
        try {
            const response = await authApi.register(formData);
            sessionStorage.setItem("registeredEmail", formData.email);
            loginSuccess(response as RegisterResponse);
            await new Promise((resolve) => setTimeout(resolve, 300));
            try {
                await refreshUser();
            } catch (refreshError) {
                console.error("Failed to refresh user data:", refreshError);
                try {
                    const authStatus = await authApi.getAuthStatus();
                    if (!(authStatus?.isAuthenticated ?? false)) {
                        console.error("Server reports user is NOT authenticated after registration!");
                        console.error("This indicates a server-side authentication issue");
                    }
                } catch (authError) {
                    console.error("Failed to check auth status:", authError);
                }
            }
            registerCallBack(response);
        } catch (error) {
            console.error("Registration failed:", error);
            throw error;
        }
    }, [loginSuccess, refreshUser]);

    const contextValue = useMemo(() => {
        return {
            getUser,
            setUser,
            getIsAuthenticated,
            setIsAuthenticated,
            getIsVerified,
            setIsVerified,
            getIsLoading,
            setIsLoading,
            getIsLoggedOut,
            login,
            logout,
            register,
            updateFirstVisit,
            refreshUser,
            getHasDashboardLayoutRun,
            setHasDashboardLayoutRun,
            getCurrentMode,
            getSignalRConnectionState,
            setSignalRConnectionState,
            mountedObjectIds: getMountedObjectIds(),
            registerMountedObject,
            unregisterMountedObject,
            isObjectMounted,
            getMountedObjectIds,
            SectionId,
            getSectionDetail,
            listSections,
            SectionIcon,
            PreviewMode,
            getFirstUncompletedPage,
            setFirstUncompletedPage,
            godMode,
            setGodMode,
            isAuthenticated: isAuthenticatedState,
            isVerified: isVerifiedState,
            isLoading: isLoadingState,
            isLoggedOut: isLoggedOutState,
        };
    }, [
        getUser,
        setUser,
        getIsAuthenticated,
        setIsAuthenticated,
        getIsVerified,
        setIsVerified,
        getIsLoading,
        setIsLoading,
        login,
        logout,
        register,
        updateFirstVisit,
        refreshUser,
        getHasDashboardLayoutRun,
        setHasDashboardLayoutRun,
        getCurrentMode,
        getSignalRConnectionState,
        setSignalRConnectionState,
        getIsLoggedOut,
        registerMountedObject,
        unregisterMountedObject,
        isObjectMounted,
        getMountedObjectIds,
        getFirstUncompletedPage,
        setFirstUncompletedPage,
        godMode,
        setGodMode,
        isAuthenticatedState,
        isVerifiedState,
        isLoadingState,
        isLoggedOutState,
    ]);

    useEffect(() => {
        return () => {
            signalRService.cleanup();
        };
    }, []);

    return (
        <GlobalContext.Provider value={contextValue}>
            {children}
        </GlobalContext.Provider>
    );
};

// Re-exported for convenience. Sourced from BASE_PATH rather than the raw VITE_BASE_URL so callers
// get the normalised value ("" at site root, no trailing slash).
export const BasePath = BASE_PATH;

export const useGlobal = () => {
    const context = useContext(GlobalContext);
    if (!context) {
        throw new Error("useGlobal must be used within a GlobalProvider");
    }
    return context;
};